import { UserAddressRequestPayload } from "../types/consultOrder.types";

// ITAM-2583 (phase-2): pure mapping from a resolved Google Place (address
// components + coordinates) to the backend UserAddressRequestPayload.
//
// Guardrails baked in here (never bypassed by the UI):
//  - `number` is copied VERBATIM from street_number; when Google gives none it
//    stays "" — we NEVER auto-invent a house number or auto-fill "bb". A
//    pharmacist may type it manually (including an explicit "bb") upstream.
//  - `floor`/`apartment`/`otherInformation` are manual-only (apartment can be
//    seeded from subpremise when present, but is otherwise "").
//  - lat/lng MUST be finite and non-zero. A 0/NaN coordinate means the place did
//    not truly resolve, so we THROW — the caller keeps newAddress null and the
//    address stays non-submittable (free text can never reach the backend).

export interface PlaceComponentLike {
  longText: string;
  shortText: string;
  types: string[];
}

/** First component whose `types` include ANY of the wanted types, in priority order. */
const pickLongText = (
  components: PlaceComponentLike[],
  wantedTypes: string[]
): string => {
  for (const wanted of wantedTypes) {
    const match = components.find((component) =>
      component.types.includes(wanted)
    );
    if (match && match.longText) {
      return match.longText;
    }
  }
  return "";
};

/** First non-empty segment of the formatted address (before the first comma). */
const firstFormattedSegment = (formatted: string): string => {
  const segment = formatted.split(",")[0];
  return segment ? segment.trim() : "";
};

export const mapPlaceToUserAddress = (
  components: PlaceComponentLike[],
  lat: number,
  lng: number,
  formatted: string
): UserAddressRequestPayload => {
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat === 0 ||
    lng === 0
  ) {
    throw new Error(
      "mapPlaceToUserAddress: unresolved place — latitude/longitude must be finite and non-zero."
    );
  }

  // Street name: route first, then progressively looser fallbacks, finally the
  // leading segment of the formatted string (never left blank when we have text).
  const name =
    pickLongText(components, [
      "route",
      "premise",
      "sublocality",
      "sublocality_level_1",
      "neighborhood",
    ]) || firstFormattedSegment(formatted);

  const number = pickLongText(components, ["street_number"]); // "" when absent — never "bb"
  const apartment = pickLongText(components, ["subpremise"]);
  const region = pickLongText(components, ["administrative_area_level_1"]);
  const city = pickLongText(components, [
    "locality",
    "postal_town",
    "administrative_area_level_2",
    "sublocality_level_1",
    "administrative_area_level_3",
  ]);
  const postalCode = pickLongText(components, ["postal_code"]);

  return {
    name,
    number,
    apartment,
    floor: "", // manual only
    region,
    city,
    postalCode,
    latitude: lat,
    longitude: lng,
    otherInformation: "", // manual only
  };
};
